import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Group,
  Text,
  Badge,
  ActionIcon,
  Menu,
  LoadingOverlay,
  Box,
  Title,
  Tooltip,
  Container,
  SimpleGrid,
  Divider,
  Paper,
  Stack,
  ScrollArea,
} from "@mantine/core";
import {
  IconPlus,
  IconEdit,
  IconDotsVertical,
  IconTrash,
  IconDeviceDesktop,
  IconForms,
  IconRefresh,
  IconShare,
  IconCheck as IconCheckStat,
  IconLock,
} from "@tabler/icons-react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";
import { useNavigate } from "react-router-dom";
import { BenchTable } from "../pages/UI";
import BenchStatCard from "../UIComponent/Components/BenchStatCard";
import BenchBreadcrumbs from "../UIComponent/Components/BenchBreadcrumbs";
import { FcExpand } from "react-icons/fc";
import { ShareLandingPageModal } from "./ShareLandingPageModal";
import BenchSettingsBreadcrumbs from "../UIComponent/Components/BenchSettingsBreadcrumbs";

interface LandingPageRecord {
  id: string;
  page_id?: string;
  form_id: string;
  company_id: string;
  created_at: any;
}

interface FormRecord {
  form_id: string;
  title: string;
}

const LandingPagesList: React.FC = () => {
  const navigate = useNavigate();
  const companyId = useAppSelector(
    (state: RootState) => state.session?.company_id,
  );
  const plan = useAppSelector(
    (state: RootState) => state.session?.company_details?.plan || "Starter",
  );

  const [landingPages, setLandingPages] = useState<LandingPageRecord[]>([]);
  const [availableForms, setAvailableForms] = useState<FormRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [activeShareUrl, setActiveShareUrl] = useState("");
  const [activeFormId, setActiveFormId] = useState<string | null>(null);

  // Plan limits
  const planLimit =
    plan.toLowerCase() === "max" ? 10 : plan.toLowerCase() === "pro" ? 5 : 1;
  const isLimitReached = landingPages.length >= planLimit;

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const pagesQ = query(
        collection(db, "customlandingpages"),
        where("company_id", "==", companyId),
      );
      const pagesSnap = await getDocs(pagesQ);
      const pagesData: LandingPageRecord[] = [];
      pagesSnap.forEach((doc) => {
        pagesData.push({ id: doc.id, ...doc.data() } as LandingPageRecord);
      });
      setLandingPages(pagesData);

      const formsQ = query(
        collection(db, "customforms"),
        where("company_id", "==", companyId),
      );
      const formsSnap = await getDocs(formsQ);
      const formsData: FormRecord[] = [];
      formsSnap.forEach((doc) => {
        formsData.push(doc.data() as FormRecord);
      });
      setAvailableForms(formsData);
    } catch (error) {
      console.error("Error fetching landing pages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    navigate(`/landingpagebuilder`);
  };

  const handleOpenShare = (page: LandingPageRecord) => {
    const baseUrl =
      process.env.REACT_APP_CURRENT_DOMAIN_NAME || window.location.origin;
    const pageKey = page.page_id || page.id;
    setActiveShareUrl(`${baseUrl}/LandingPage/pageHtml?page_id=${pageKey}`);
    setActiveFormId(page.form_id || null);
    setIsShareModalOpen(true);
  };

  const statsData = [
    {
      title: "Total Pages",
      count: landingPages.length,
      description: "All landing pages created",
      icon: <IconDeviceDesktop size={22} color="#1c7ed6" />,
      bgColor: "#e7f5ff",
      textColor: "#1c7ed6",
      descColor: "#5c7cfa",
    },
    {
      title: "Allowed Pages",
      count: planLimit,
      description: `Based on your ${plan} plan`,
      icon: <IconCheckStat size={22} color="#2b8a3e" />,
      bgColor: "#ebfbee",
      textColor: "#2b8a3e",
      descColor: "#40c057",
    },
    {
      title: "Available Forms",
      count: availableForms.length,
      description: "Forms you can link",
      icon: <IconForms size={22} color="#c2255c" />,
      bgColor: "#fff0f6",
      textColor: "#c2255c",
      descColor: "#d81b60",
    },
  ];

  const breadcrumbslinks = [{ label: "Landing Pages" }];

  return (
    <div className="page">
      <Container size="lg">
        <LoadingOverlay visible={isLoading} />

        <Box
          sx={(theme) => ({
            position: "sticky",
            top: 110,
            zIndex: 100,
            backgroundColor: "#FAFAFA",
            paddingTop: theme.spacing.sm,
            marginTop: `-${theme.spacing.sm}`,
          })}
        >
          <SimpleGrid cols={2}>
            <BenchSettingsBreadcrumbs links={breadcrumbslinks} />
            <Group position="right">
              <Tooltip
                label={
                  isLimitReached
                    ? `You've reached your limit of ${planLimit} pages. Upgrade your plan.`
                    : ""
                }
                disabled={!isLimitReached}
              >
                <div>
                  <Button
                    onClick={handleCreate}
                    disabled={isLimitReached}
                    size="md"
                  >
                    {isLimitReached ? (
                      <>
                        <IconLock size={16} />
                        Create
                      </>
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </Tooltip>
            </Group>
          </SimpleGrid>
          <Divider my="sm" />
        </Box>

        <BenchStatCard data={statsData} />

        {/* Empty State */}
        {!isLoading && landingPages.length === 0 && (
          <Paper withBorder p="xl" ta="center" mt="md">
            <Stack spacing="md">
              <IconDeviceDesktop
                size={64}
                color="var(--mantine-color-gray-5)"
                style={{ margin: "0 auto" }}
              />
              <div>
                <Title order={4} mb="xs">
                  No Landing Pages Created
                </Title>
                <Text color="dimmed">
                  You haven't created any landing pages yet. Design a
                  high-converting page for your forms.
                </Text>
              </div>
              <Button
                onClick={handleCreate}
                leftIcon={<IconPlus size={16} />}
                size="sm"
                maw={300}
                mx="auto"
                disabled={isLimitReached}
              >
                Create Your First Page
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Table View */}
        {!isLoading && landingPages.length > 0 && (
          <ScrollArea mt="md">
            <BenchTable>
              <thead>
                <tr>
                  <th>Page Name</th>
                  <th>Status</th>
                  <th>Date Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {landingPages.map((page) => {
                  const formTitle =
                    availableForms.find((f) => f.form_id === page.form_id)
                      ?.title || "Unknown Form";

                  return (
                    <tr
                      key={page.id}
                      onClick={() =>
                        navigate(
                          `/landingpagebuilder/${page.page_id || page.id}`,
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <Text fw={500}>{formTitle}</Text>
                      </td>
                      <td>
                        <Badge color="green" variant="light">
                          Active
                        </Badge>
                      </td>
                      <td>
                        <Text size="sm">
                          {page.created_at?.toDate
                            ? page.created_at.toDate().toLocaleDateString()
                            : "Just now"}
                        </Text>
                      </td>
                      <td style={{ width: "80px" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Menu shadow="md" width={200} withinPortal>
                            <Menu.Target>
                              <ActionIcon variant="default">
                                <FcExpand size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                icon={<IconEdit size={14} />}
                                onClick={() =>
                                  navigate(
                                    `/landingpagebuilder/${page.page_id || page.id}`,
                                  )
                                }
                              >
                                Edit Design
                              </Menu.Item>
                              <Menu.Item
                                icon={<IconShare size={14} />}
                                onClick={() => handleOpenShare(page)}
                              >
                                Share Page
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </BenchTable>
          </ScrollArea>
        )}
      </Container>

      <ShareLandingPageModal
        opened={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        savedPageUrl={activeShareUrl}
        availableForms={availableForms.map((f) => ({
          value: f.form_id,
          label: f.title,
        }))}
        selectedFormIdUrl={activeFormId}
        onSelectFormIdUrl={setActiveFormId}
      />
    </div>
  );
};

export default LandingPagesList;
